// useMirrorState.js
// Mirrors division (08 00 01) - UI state and simple handlers
// API handlers remain in ProjectDetail.js due to external dependencies

import { useState, useCallback } from 'react';

// Default marker color for mirrors
const DEFAULT_MARKER_COLOR = '#3B82F6'; // Blue

export const useMirrorState = () => {
  // ============================================
  // OPERATION LOADING STATE (prevents double-submit)
  // ============================================
  const [mirrorOperationLoading, setMirrorOperationLoading] = useState(false);
  
  // ============================================
  // SELECTION STATES
  // ============================================
  const [selectedMirrorTypeIds, setSelectedMirrorTypeIds] = useState([]);
  const [selectedMirrorScheduleIds, setSelectedMirrorScheduleIds] = useState([]);
  
  // ============================================
  // MIRROR TYPE DIALOG STATE
  // ============================================
  const [showMirrorTypeDialog, setShowMirrorTypeDialog] = useState(false);
  const [editingMirrorType, setEditingMirrorType] = useState(null);
  const [newMirrorType, setNewMirrorType] = useState({
    name: '',
    manufacturer: '',
    model: '',
    mirrorThickness: '',
    frameMaterial: '',
    frameFinish: '',
    description: '',
    mirrorWarrantyYears: null,
    installationWarrantyYears: null,
    markerColor: DEFAULT_MARKER_COLOR
  });
  
  // ============================================
  // MIRROR SCHEDULE DIALOG STATE
  // ============================================
  const [showMirrorScheduleDialog, setShowMirrorScheduleDialog] = useState(false);
  const [editingMirrorSchedule, setEditingMirrorSchedule] = useState(null);
  const [newMirrorSchedule, setNewMirrorSchedule] = useState({
    label: '',
    mirrorTypeId: '',
    location: '',
    notes: '',
    mirrorWidth_in: null,
    mirrorHeight_in: null
  });
  
  // ============================================
  // MIRROR SCHEDULE DETAIL MODAL STATE
  // ============================================
  const [showMirrorScheduleDetailModal, setShowMirrorScheduleDetailModal] = useState(false);
  const [selectedMirrorScheduleForDetail, setSelectedMirrorScheduleForDetail] = useState(null);
  const [mirrorSchedulePictureUploading, setMirrorSchedulePictureUploading] = useState(false);
  
  // ============================================
  // MIRROR SCHEDULE SEARCH/FILTER STATE
  // ============================================
  const [mirrorScheduleSearch, setMirrorScheduleSearch] = useState('');
  const [mirrorScheduleFilters, setMirrorScheduleFilters] = useState({ type: '', location: '' });
  const [mirrorScheduleSortBy, setMirrorScheduleSortBy] = useState('createdAt_desc');

  // ============================================
  // MIRROR FLOOR PLAN (ELEVATION) STATE
  // ============================================
  const [selectedMirrorElevation, setSelectedMirrorElevation] = useState(null);
  const [selectedMirrorElevationIds, setSelectedMirrorElevationIds] = useState([]);
  const [mirrorBulkMarkerMode, setMirrorBulkMarkerMode] = useState(false);
  const [mirrorBulkMarkerSchedule, setMirrorBulkMarkerSchedule] = useState('');
  const [pendingMirrorBulkMarkers, setPendingMirrorBulkMarkers] = useState([]);
  const [savingMirrorBulkMarkers, setSavingMirrorBulkMarkers] = useState(false);
  
  // ============================================
  // MIRROR TYPE PICTURE STATE
  // ============================================
  const [pendingMirrorTypePicture, setPendingMirrorTypePicture] = useState(null);
  const [mirrorTypePictureUploading, setMirrorTypePictureUploading] = useState(null);
  const [mirrorTypePictureUrl, setMirrorTypePictureUrl] = useState(null);
  const [selectedMirrorTypeForView, setSelectedMirrorTypeForView] = useState(null);
  const [showMirrorTypeViewDialog, setShowMirrorTypeViewDialog] = useState(false);
  
  // ============================================
  // DELETE CONFIRMATION STATE
  // ============================================
  const [showDeleteMirrorTypeModal, setShowDeleteMirrorTypeModal] = useState(false);
  const [deleteMirrorTypeId, setDeleteMirrorTypeId] = useState(null);
  
  // ============================================
  // RESET FUNCTIONS
  // ============================================
  const resetNewMirrorType = useCallback(() => {
    setNewMirrorType({
      name: '',
      manufacturer: '',
      model: '',
      mirrorThickness: '',
      frameMaterial: '',
      frameFinish: '',
      description: '',
      mirrorWarrantyYears: null,
      installationWarrantyYears: null,
      markerColor: DEFAULT_MARKER_COLOR
    });
    setPendingMirrorTypePicture(null);
    setMirrorTypePictureUrl(null);
  }, []);
  
  const resetNewMirrorSchedule = useCallback(() => {
    setNewMirrorSchedule({
      label: '',
      mirrorTypeId: '',
      location: '',
      notes: '',
      mirrorWidth_in: null,
      mirrorHeight_in: null
    });
  }, []);
  
  const resetMirrorTypeDialog = useCallback(() => {
    setShowMirrorTypeDialog(false);
    setEditingMirrorType(null);
    resetNewMirrorType();
  }, [resetNewMirrorType]);
  
  const resetMirrorScheduleDialog = useCallback(() => {
    setShowMirrorScheduleDialog(false);
    setEditingMirrorSchedule(null);
    resetNewMirrorSchedule();
  }, [resetNewMirrorSchedule]);
  
  // ============================================
  // OPEN DIALOG FOR EDIT
  // ============================================
  const openEditMirrorTypeDialog = useCallback((mirrorType) => {
    setEditingMirrorType(mirrorType);
    setNewMirrorType({
      name: mirrorType.name || '',
      manufacturer: mirrorType.manufacturer || '',
      model: mirrorType.model || '',
      mirrorThickness: mirrorType.mirrorThickness || '',
      frameMaterial: mirrorType.frameMaterial || '',
      frameFinish: mirrorType.frameFinish || '',
      description: mirrorType.description || '',
      mirrorWarrantyYears: mirrorType.mirrorWarrantyYears || null,
      installationWarrantyYears: mirrorType.installationWarrantyYears || null,
      markerColor: mirrorType.markerColor || DEFAULT_MARKER_COLOR
    });
    setMirrorTypePictureUrl(
      mirrorType.pictureCloudinary?.urls?.medium || 
      mirrorType.pictureUrl || 
      null
    );
    setShowMirrorTypeDialog(true);
  }, []);
  
  // ============================================
  // CONFIRM DELETE
  // ============================================
  const confirmDeleteMirrorType = useCallback((mirrorTypeId) => {
    setDeleteMirrorTypeId(mirrorTypeId);
    setShowDeleteMirrorTypeModal(true);
  }, []);
  
  const cancelDeleteMirrorType = useCallback(() => {
    setDeleteMirrorTypeId(null);
    setShowDeleteMirrorTypeModal(false);
  }, []);
  
  // ============================================
  // SELECTION HANDLERS
  // ============================================
  const toggleMirrorTypeSelection = useCallback((id, event) => {
    if (event?.shiftKey) {
      // Shift+click for range selection would need list context
      setSelectedMirrorTypeIds(prev => 
        prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      );
    } else {
      setSelectedMirrorTypeIds(prev => 
        prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      );
    }
  }, []);
  
  const selectAllMirrorTypes = useCallback((mirrorTypes) => {
    if (selectedMirrorTypeIds.length === mirrorTypes.length) {
      setSelectedMirrorTypeIds([]);
    } else {
      setSelectedMirrorTypeIds(mirrorTypes.map(mt => mt.id));
    }
  }, [selectedMirrorTypeIds]);
  
  const clearMirrorTypeSelections = useCallback(() => {
    setSelectedMirrorTypeIds([]);
  }, []);
  
  // Mirror Schedule selection handlers
  const toggleMirrorScheduleSelection = useCallback((id, event) => {
    setSelectedMirrorScheduleIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  }, []);
  
  const selectAllMirrorSchedules = useCallback((mirrorSchedules) => {
    if (selectedMirrorScheduleIds.length === mirrorSchedules.length) {
      setSelectedMirrorScheduleIds([]);
    } else {
      setSelectedMirrorScheduleIds(mirrorSchedules.map(ms => ms.id));
    }
  }, [selectedMirrorScheduleIds]);
  
  const clearMirrorScheduleSelections = useCallback(() => {
    setSelectedMirrorScheduleIds([]);
  }, []);
  
  // Open mirror schedule for editing
  const openEditMirrorScheduleDialog = useCallback((schedule) => {
    setEditingMirrorSchedule(schedule);
    setNewMirrorSchedule({
      label: schedule.label || '',
      mirrorTypeId: schedule.mirrorTypeId || '',
      location: schedule.location || '',
      notes: schedule.notes || '',
      mirrorWidth_in: schedule.mirrorWidth_in || null,
      mirrorHeight_in: schedule.mirrorHeight_in || null
    });
    setShowMirrorScheduleDialog(true);
  }, []);
  
  // Open mirror schedule detail modal
  const openMirrorScheduleDetail = useCallback((schedule) => {
    setSelectedMirrorScheduleForDetail(schedule);
    setShowMirrorScheduleDetailModal(true);
  }, []);
  
  // Close mirror schedule detail modal
  const closeMirrorScheduleDetail = useCallback(() => {
    setSelectedMirrorScheduleForDetail(null);
    setShowMirrorScheduleDetailModal(false);
  }, []);
  
  return {
    // Operation loading
    mirrorOperationLoading,
    setMirrorOperationLoading,
    
    // Selection states
    selectedMirrorTypeIds,
    setSelectedMirrorTypeIds,
    selectedMirrorScheduleIds,
    setSelectedMirrorScheduleIds,
    
    // Dialog states - Mirror Type
    showMirrorTypeDialog,
    setShowMirrorTypeDialog,
    editingMirrorType,
    setEditingMirrorType,
    newMirrorType,
    setNewMirrorType,
    
    // Dialog states - Mirror Schedule
    showMirrorScheduleDialog,
    setShowMirrorScheduleDialog,
    editingMirrorSchedule,
    setEditingMirrorSchedule,
    newMirrorSchedule,
    setNewMirrorSchedule,
    
    // Mirror Schedule Detail Modal
    showMirrorScheduleDetailModal,
    setShowMirrorScheduleDetailModal,
    selectedMirrorScheduleForDetail,
    setSelectedMirrorScheduleForDetail,
    mirrorSchedulePictureUploading,
    setMirrorSchedulePictureUploading,
    
    // Mirror Schedule Search/Filter
    mirrorScheduleSearch,
    setMirrorScheduleSearch,
    mirrorScheduleFilters,
    setMirrorScheduleFilters,
    mirrorScheduleSortBy,
    setMirrorScheduleSortBy,
    
    // Mirror Floor Plan (Elevation) State
    selectedMirrorElevation,
    setSelectedMirrorElevation,
    selectedMirrorElevationIds,
    setSelectedMirrorElevationIds,
    mirrorBulkMarkerMode,
    setMirrorBulkMarkerMode,
    mirrorBulkMarkerSchedule,
    setMirrorBulkMarkerSchedule,
    pendingMirrorBulkMarkers,
    setPendingMirrorBulkMarkers,
    savingMirrorBulkMarkers,
    setSavingMirrorBulkMarkers,
    
    // Picture states
    pendingMirrorTypePicture,
    setPendingMirrorTypePicture,
    mirrorTypePictureUploading,
    setMirrorTypePictureUploading,
    mirrorTypePictureUrl,
    setMirrorTypePictureUrl,
    selectedMirrorTypeForView,
    setSelectedMirrorTypeForView,
    showMirrorTypeViewDialog,
    setShowMirrorTypeViewDialog,
    
    // Delete confirmation
    showDeleteMirrorTypeModal,
    setShowDeleteMirrorTypeModal,
    deleteMirrorTypeId,
    setDeleteMirrorTypeId,
    
    // Functions - Mirror Type
    resetNewMirrorType,
    resetMirrorTypeDialog,
    openEditMirrorTypeDialog,
    confirmDeleteMirrorType,
    cancelDeleteMirrorType,
    toggleMirrorTypeSelection,
    selectAllMirrorTypes,
    clearMirrorTypeSelections,
    
    // Functions - Mirror Schedule
    resetNewMirrorSchedule,
    resetMirrorScheduleDialog,
    openEditMirrorScheduleDialog,
    openMirrorScheduleDetail,
    closeMirrorScheduleDetail,
    toggleMirrorScheduleSelection,
    selectAllMirrorSchedules,
    clearMirrorScheduleSelections
  };
};

export default useMirrorState;
